/**
 * «Экспортировать план» (the app's NewFloorPlanSnapshotPopup and FloorPlanTypesList): the floor in view as a picture in
 * one of the app's three drawings — the plan as it is drawn, a black-and-white plan with the furniture for an estate
 * agent, and an architect's plan with every wall's length —, each shown small as it opens and saved as a PNG when
 * chosen (`renderPlanImage`). The app keeps two of them for paying users; here all three are there.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { Level } from '../../core/hp/types';
import { renderPlanImage, type PlanExportStyle } from '../../render/plan/PlanView';
import type { PlanFurnitureLayer } from '../../render/three/PlanTopView';
import type { Stairwell } from '../../core/geometry/stairwells';
import { Icon } from '../Icon';
import { t, type TranslationKey } from '../../i18n/I18n';

/** The app's three, in its order, with its descriptions (#RegularSnapshot, #ProRealEstate, #ProDesignProject). */
const STYLES: { style: PlanExportStyle; title: TranslationKey; detail: TranslationKey; file: TranslationKey }[] = [
  { style: 'colour', title: '#PlanExportColour', detail: '#PlanExportColourDetail', file: '#PlanExportColourFile' },
  { style: 'furniture', title: '#PlanExportFurniture', detail: '#PlanExportFurnitureDetail', file: '#PlanExportFurnitureFile' },
  { style: 'architect', title: '#PlanExportArchitect', detail: '#PlanExportArchitectDetail', file: '#PlanExportArchitectFile' },
];

export function PlanExportDialog({ level, levelTitle, projectName, dimensions, furniture, wells, onClose }: {
  level: Level; levelTitle: string; projectName: string; dimensions: boolean;
  furniture: () => PlanFurnitureLayer | null; wells: readonly Stairwell[]; onClose: () => void;
}) {
  const [previews, setPreviews] = useState<Partial<Record<PlanExportStyle, string>>>({});
  const [busy, setBusy] = useState<PlanExportStyle | null>(null), [failed, setFailed] = useState('');
  const section = useRef<HTMLElement>(null), live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  // The three small pictures, one after another, so the dialog is up at once and fills as they come.
  useEffect(() => {
    let cancelled = false;
    const urls: string[] = [];
    (async () => {
      for (const { style } of STYLES) {
        const canvas = await renderPlanImage(level, style, { furniture: furniture(), wells, dimensions, longSide: 420, dpr: 1, wait: 2500 });
        if (cancelled || !canvas) return;
        const url = canvas.toDataURL('image/png');
        urls.push(url);
        setPreviews(current => ({ ...current, [style]: url }));
      }
    })().catch(() => { if (!cancelled) setFailed(t('#PlanExportDrawFailed')); });
    return () => { cancelled = true; };
  }, [level]);
  useEffect(() => { section.current?.querySelector<HTMLButtonElement>('.plan-export-card')?.focus({ preventScroll: true }); }, []);
  const save = async (style: PlanExportStyle, file: TranslationKey) => {
    if (busy) return;
    setBusy(style); setFailed('');
    try {
      const canvas = await renderPlanImage(level, style, { furniture: furniture(), wells, dimensions, longSide: 1600, dpr: 2 });
      const blob = canvas && await new Promise<Blob | null>(done => canvas.toBlob(done, 'image/png'));
      if (!blob) throw new Error('empty');
      const url = URL.createObjectURL(blob), link = document.createElement('a');
      link.href = url; link.download = `${projectName || t('#PlanExportDefaultName')} — ${levelTitle} — ${t(file)}.png`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      if (live.current) setFailed(t('#PlanExportSaveFailed'));
    } finally {
      if (live.current) setBusy(null);
    }
  };
  const keyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose(); } };
  return <div className="modal-backdrop" onClick={onClose}><section ref={section} className="plan-export" role="dialog" aria-modal="true" aria-label={t('#ExportFloorplan')} onClick={event => event.stopPropagation()} onKeyDown={keyDown}>
    <header className="stats-head"><h2>{t('#ExportFloorplan')}</h2><button type="button" className="icon-btn" aria-label={t('#PlanExportClose')} onClick={onClose}><Icon name="close" size={20} /></button></header>
    <p className="plan-export-note">{t('#PlanExportNote', levelTitle)}</p>
    <div className="plan-export-cards">{STYLES.map(({ style, title, detail, file }) =>
      <button key={style} type="button" className="plan-export-card" aria-busy={busy === style} disabled={!!busy && busy !== style} onClick={() => void save(style, file)}>
        <span className="plan-export-picture">{previews[style] ? <img src={previews[style]} alt="" /> : <span className="material-skeleton" aria-hidden="true" />}
          {busy === style && <span className="plan-export-busy"><span className="room-shapes-spinner" aria-hidden="true" />{t('#PlanExportPreparing')}</span>}</span>
        <span className="plan-export-text"><strong>{t(title)}</strong><small>{t(detail)}</small></span>
        <Icon name="download" size={18} />
      </button>)}</div>
    {failed && <p className="inline-error" role="alert">{failed}</p>}
  </section></div>;
}
